import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { isMotionGraphicsSceneRequested } from './motion-graphics/motionGraphicsSceneDesign';
import { computeSceneShortIds } from './scene-addressing';
import { resolveInsertSlotAfter } from './scene-group-addressing';
import { validateSceneDesignId } from './scene-design-validation';
import { VideoStateTool } from './VideoStateTool';

import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { addCodeoSceneOperation } from 'js/src/luminary/html-asset-operations';
import {
    addSceneWithDesignOperation,
    enableOutroSceneGroupOperation,
    saveRawLuminary,
} from 'js/src/luminary/index';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const addSceneSchema = z.object({
    after_scene_group_id: z
        .string()
        .optional()
        .describe(
            'The stable scene_group_id of the scene group the new scenes should be inserted immediately after. Omit to insert them at the very start of the video.',
        ),
    scenes: z
        .array(
            z.object({
                scene_design_id: z
                    .string()
                    .describe(
                        'The scene design ID to use for this scene (e.g., "template_id:design_name")',
                    ),
            }),
        )
        .min(1)
        .describe('The scenes to add, in the order they should appear in the video.'),
});
export type AddSceneArgs = z.infer<typeof addSceneSchema>;

interface AddedScene {
    sceneId: string;
    sceneDesignId?: string;
    isMotionGraphics: boolean;
    /** Outros are force-placed at the end of the video rather than at the requested slot. */
    isOutro?: boolean;
}

export class AddSceneTool extends VideoStateTool<typeof addSceneSchema> {
    protected override readonly schema = addSceneSchema;
    protected override readonly toolName = TOOL_NAMES.addScene;
    protected override readonly toolDescription =
        'Add one or more new scenes to the video, each using the specified scene design. Each new scene is its own scene group.';

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AddSceneArgs>;
                if ((parsed.scenes?.length ?? 0) > 1) {
                    return `Adding ${parsed.scenes!.length} scenes`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Adding scene';
    }

    protected override onEditApplied(args: AddSceneArgs): void {
        // Inserting a scene at the very start (no anchor) changes the first scene shown in the thumbnail.
        if (args.after_scene_group_id == null || args.after_scene_group_id.trim() === '') {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'add_photo_alternate';
    }

    /**
     * The motion-graphics branch: create the scene empty, because the graphic itself is authored
     * separately by the design tool. Returns the new scene's id, or an error message.
     */
    private addMotionGraphicsScene(slot: number): AddedScene | { error: string } {
        const { addCodeoScene } = makeOperationsCallable(
            { addCodeoSceneOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const created = addCodeoScene(slot);
        if (!created) {
            return { error: 'could not create a motion graphics scene.' };
        }

        this.startMotionGraphicsSceneBuilding(created.sceneId);

        return { sceneId: created.sceneId, isMotionGraphics: true };
    }

    /** Add one stock-design scene at `slot`, or report why the design id was refused. */
    private addDesignedScene(
        slot: number,
        sceneDesignId: string,
        parentActionGroup: string,
    ): AddedScene | { error: string } {
        const validation = validateSceneDesignId(this.luminary, sceneDesignId);
        if (validation.error) {
            // validateSceneDesignId's messages are already prefixed with "Error: ".
            return { error: validation.error.replace(/^Error:\s*/, '') };
        }

        // A video is only ever allowed one outro, and it belongs at the end (before branding),
        // so route outro designs through enableOutroSceneGroup rather than the requested slot.
        if (validation.sceneCategory === VideoData.CATEGORY_OUTRO) {
            return this.addOutroScene(sceneDesignId, parentActionGroup);
        }

        const { addSceneWithDesign } = makeOperationsCallable(
            { addSceneWithDesignOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const { sceneId, sceneDesignId: appliedDesignId } = addSceneWithDesign(slot, sceneDesignId);

        // Mark the new scene as "building" so the build preview blurs it
        // until the content it needs lands (set_text/set_media/image_ask).
        this.startSceneBuilding(sceneId);

        return { sceneId, sceneDesignId: appliedDesignId, isMotionGraphics: false };
    }

    /**
     * Add the video's single outro scene at the end of the video. Refuses when the video
     * already has one, so the agent never gets a success for a scene it didn't add.
     */
    private addOutroScene(
        sceneDesignId: string,
        parentActionGroup: string,
    ): AddedScene | { error: string } {
        if (this.luminary.getOutroSceneGroup() != null) {
            return {
                error:
                    'the video already has an outro, and a video can only have one. ' +
                    'To use this outro design instead, delete the existing outro scene group with ' +
                    `${TOOL_NAMES.deleteSceneGroup} first; otherwise pick a non-outro scene design.`,
            };
        }

        const { enableOutroSceneGroup } = makeOperationsCallable(
            { enableOutroSceneGroupOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        enableOutroSceneGroup(false, parentActionGroup, sceneDesignId);

        const outro = this.luminary.getOutroSceneGroup();
        if (!outro) {
            return { error: `could not add an outro scene for "${sceneDesignId}".` };
        }

        // Mark it "building" like addDesignedScene's other branch, so the build preview
        // blurs it until its content lands.
        this.startSceneBuilding(outro.scene.id);

        return {
            sceneId: outro.scene.id,
            sceneDesignId: outro.scene.sceneDesign,
            isMotionGraphics: false,
            isOutro: true,
        };
    }

    /**
     * Report the batch back to the agent: every added scene by the scene_id it is addressed by
     * afterwards, plus any request that was refused, named so a retry can target just that one.
     */
    private describeOutcome(
        added: AddedScene[],
        failures: string[],
        afterSceneGroupId?: string,
    ): string {
        const shortIds = computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id));
        const shortIdOf = (sceneId: string) => shortIds.get(sceneId) ?? sceneId;

        if (added.length === 0) {
            return `Error adding scene: ${failures.join('; ')}`;
        }

        const placement =
            afterSceneGroupId && afterSceneGroupId.trim() !== ''
                ? `right after scene group ${afterSceneGroupId}`
                : 'at the start of the video';
        const describeScene = (scene: AddedScene) => {
            const label = scene.isMotionGraphics
                ? `scene_id ${shortIdOf(scene.sceneId)} (empty motion graphics scene)`
                : `scene_id ${shortIdOf(scene.sceneId)} (scene design "${scene.sceneDesignId}")`;
            // An outro ignores the requested slot, so say where it actually went.
            return scene.isOutro ? `${label} — placed at the end of the video` : label;
        };

        // Outros never land at the batch's placement, so don't let it speak for them: each
        // scene's own description says where an outro went.
        const slottedCount = added.filter((scene) => !scene.isOutro).length;
        const summary =
            added.length === 1
                ? `Success: Added new scene (${describeScene(added[0])})${
                      added[0].isOutro ? '.' : ` ${placement}.`
                  }`
                : `Success: Added ${added.length} new scenes${
                      slottedCount > 0 ? ` ${placement}` : ''
                  }, in this order:\n` +
                  added.map((scene) => `  - ${describeScene(scene)}`).join('\n');

        const motionGraphicsShortIds = added
            .filter((scene) => scene.isMotionGraphics)
            .map((scene) => shortIdOf(scene.sceneId));
        const motionGraphicsFollowUp =
            motionGraphicsShortIds.length > 0
                ? `\n\nThe motion graphics scene(s) (${motionGraphicsShortIds.join(', ')}) show nothing yet — ` +
                  `you author each one: ${TOOL_NAMES.writePlan} then ` +
                  `${TOOL_NAMES.writeHtmlAsset}, per scene_id.`
                : '';
        const failureReport =
            failures.length > 0
                ? `\n\nNot added (fix and retry just these): ${failures.join('; ')}`
                : '';

        return `${summary}${motionGraphicsFollowUp}${failureReport}`;
    }

    public async execute(args: AddSceneArgs, context: ToolExecutionContext) {
        const { after_scene_group_id, scenes } = args;
        const { parentActionGroup } = context;

        // Resolve the anchor slot up front (fail-closed on an unknown anchor id).
        const firstSlot = resolveInsertSlotAfter(this.luminary, after_scene_group_id);

        const added: AddedScene[] = [];
        const failures: string[] = [];

        // Outros are force-placed at the end of the video, so they never occupy a slot. Only
        // count the scenes actually inserted at one, or an outro earlier in the batch would push
        // every scene after it one position too far along.
        let insertedAtSlot = 0;

        scenes.forEach(({ scene_design_id }, requestIndex) => {
            // Each scene goes one slot further along, so the batch lands in the order given rather
            // than every scene stacking onto the anchor in reverse.
            const slot = firstSlot + insertedAtSlot;
            const describeRequest = `scenes[${requestIndex}] ("${scene_design_id}")`;

            try {
                if (!scene_design_id) {
                    failures.push(
                        `${describeRequest}: scene_design_id is required (e.g., "template_id:design_name")`,
                    );
                    return;
                }

                const result = isMotionGraphicsSceneRequested(scene_design_id)
                    ? this.addMotionGraphicsScene(slot)
                    : this.addDesignedScene(slot, scene_design_id, parentActionGroup);

                if ('error' in result) {
                    failures.push(`${describeRequest}: ${result.error}`);
                    return;
                }
                if (!result.isOutro) {
                    insertedAtSlot++;
                }
                added.push(result);
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                failures.push(`${describeRequest}: ${errorMessage}`);
            }
        });

        // One checkpoint for the whole batch — the scenes were added together and are undone together.
        if (added.length > 0) {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        }

        return this.describeOutcome(added, failures, after_scene_group_id);
    }
}
